// On-screen UI label for a field, matched from the rendered DOM first and the i18n
// resources second. Matching is always exact — an unmatched field keeps no label.
//
// This used to take a generated AutoMapper alias file. That file is gone: it was produced by
// a developer-run script, so it went stale, and the suffix fallback below already covers the
// renames worth covering. An alias that neither exact nor suffix matching can reach is left
// unlabelled rather than guessed, and a curated uiLabel always overrides anyway.

// Control names are camelCase, DB columns PascalCase, so both are flattened before matching.
export const normalizeKey = (s) => (s || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');

// The \u00a0 strip is for the placeholder the form components render into empty labels.
const cleanLabel = (raw) =>
    (raw || '')
        .toString()
        .replace(/ /g, ' ')
        .replace(/[\s:*]+$/, '')
        .replace(/\s+/g, ' ')
        .trim();

// The drawer overlays the right edge but the page behind keeps real viewport coordinates,
// so controls under it still count as on screen.
const isOnScreen = (el) => {
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return false;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    return r.bottom > 0 && r.right > 0 && r.top < vh && r.left < vw;
};

const labelForInput = (el) => {
    if (el.labels && el.labels.length) return el.labels[0].textContent;
    const ariaId = el.getAttribute('aria-labelledby');
    if (ariaId) {
        const node = document.getElementById(ariaId.split(/\s+/)[0]);
        if (node) return node.textContent;
    }
    return el.getAttribute('aria-label') || '';
};

// Scans the live document for rendered field labels. excludeEl is the panel's own root, so
// its own controls aren't scraped.
export const scanOnScreenLabels = (excludeEl) => {
    const map = new Map();
    if (typeof document === 'undefined') return map;

    const add = (rawKey, rawLabel, onScreen) => {
        const key = normalizeKey(rawKey);
        const label = cleanLabel(rawLabel);
        if (!key || !label) return;
        const existing = map.get(key);
        // First match wins, but an on-screen sighting upgrades an earlier off-screen one.
        if (!existing) map.set(key, { label, onScreen });
        else if (onScreen && !existing.onScreen) map.set(key, { label, onScreen: true });
    };

    const skip = (el) => excludeEl && excludeEl.contains(el);

    document.querySelectorAll('input[name], textarea[name], select[name]').forEach((el) => {
        if (skip(el) || el.type === 'hidden') return;
        add(el.name || el.id, labelForInput(el), isOnScreen(el));
    });

    document.querySelectorAll('.MuiDataGrid-columnHeader[data-field]').forEach((el) => {
        if (skip(el)) return;
        const title = el.querySelector('.MuiDataGrid-columnHeaderTitle');
        add(el.getAttribute('data-field'), title && title.textContent, isOnScreen(el));
    });

    return map;
};

// The first path segment is the "group" (e.g. tableHeadCells.students).
const forEachLeaf = (obj, group, cb) => {
    if (!obj || typeof obj !== 'object') return;
    Object.keys(obj).forEach((k) => {
        const v = obj[k];
        if (typeof v === 'string') cb(group == null ? k : group, k, v);
        else if (v && typeof v === 'object') forEachLeaf(v, group == null ? k : group, cb);
    });
};

// Memoised on (language, bundles-present, route) — the only things that change it.
let i18nIndexCache = null;

// Label index from the i18n resources. Two groups at the same priority that disagree on a
// key are treated as ambiguous and skipped, rather than picking one entity's label.
export const buildI18nLabelIndex = (i18n, routeHint) => {
    if (!i18n || typeof i18n.getResourceBundle !== 'function') return new Map();
    const lng = i18n.language || 'en-US';
    const routeNorm = normalizeKey(routeHint);

    const hasBundle = (ns) => !!(i18n.hasResourceBundle && i18n.hasResourceBundle(lng, ns));
    const cacheKey = `${lng}|${hasBundle('tableHeadCells')}|${hasBundle('translation')}|${routeNorm}`;
    if (i18nIndexCache && i18nIndexCache.key === cacheKey) return i18nIndexCache.result;

    const index = new Map(); // normKey -> { [priority]: { label, conflicted } }

    const set = (col, label, priority) => {
        const key = normalizeKey(col);
        const clean = cleanLabel(label);
        if (!key || !clean) return;
        let slots = index.get(key);
        if (!slots) { slots = {}; index.set(key, slots); }
        const slot = slots[priority];
        if (!slot) slots[priority] = { label: clean, conflicted: false };
        else if (!slot.conflicted && slot.label.toLowerCase() !== clean.toLowerCase()) slot.conflicted = true;
    };

    // Route-matched = the group's (>=4 char) name appears in the route.
    const isRouteGroup = (group) => {
        const g = normalizeKey(group);
        return g.length >= 4 && routeNorm.indexOf(g) !== -1;
    };

    let headCells;
    let translation;
    try { headCells = i18n.getResourceBundle(lng, 'tableHeadCells'); } catch (e) { headCells = null; }
    try { translation = i18n.getResourceBundle(lng, 'translation'); } catch (e) { translation = null; }

    forEachLeaf(headCells, null, (group, col, label) => set(col, label, isRouteGroup(group) ? 0 : 2));
    if (translation && translation.commonLabel) {
        forEachLeaf(translation.commonLabel, 'commonLabel', (g, col, label) => set(col, label, 1));
    }

    const out = new Map();
    index.forEach((slots, k) => {
        for (let p = 0; p <= 2; p += 1) {
            const slot = slots[p];
            if (slot && !slot.conflicted) { out.set(k, slot.label); break; }
        }
    });
    i18nIndexCache = { key: cacheKey, result: out };
    return out;
};

// Long enough that generic tails ("id", "name", "date") can't bind the wrong column.
const MIN_SUFFIX = 5;

const exactLookup = (map, keys) => {
    if (!map) return undefined;
    for (let i = 0; i < keys.length; i += 1) {
        const v = map.get(keys[i]);
        if (v) return v;
    }
    return undefined;
};

// Annotates each field with { uiLabel, uiLabelSource, onScreen }, preferring a curated
// label, then the DOM scrape, then i18n. onScreen is only set when the control-to-column
// pairing is unambiguous: one rendered control cannot vouch for the same-named column in
// several tables at once.
export const resolveFieldLabels = (fields, domMap, i18nIndex) => {
    const metas = (fields || []).map((f) => {
        const key = normalizeKey(f.column);
        // Grid head-cell ids sometimes append "Text" to a decoded column.
        const altKey = key.endsWith('text') ? key.slice(0, -4) : null;

        const keys = [key, ...(altKey ? [altKey] : [])];
        const domKey = keys.find((k) => domMap.has(k)) || null;
        return { f, key, altKey, keys, domKey };
    });

    // A column name shared by several of the page's tables matches the one rendered control:
    // the label is safe to share, but none can be promoted on-screen.
    const claims = new Map(); // domKey -> number of fields claiming it
    metas.forEach((m) => { if (m.domKey) claims.set(m.domKey, (claims.get(m.domKey) || 0) + 1); });

    // Suffix fallback for a UI that drops a prefix the DB keeps (`cancelled` backs
    // `ApplicantCancelled`). Binds only when unique in both directions, so
    // ApplicantCancelled + EnrollmentCancelled binds nothing rather than guessing.
    const wantedBy = new Map(); // domKey -> indexes of metas that suffix-match it
    metas.forEach((m, i) => {
        if (m.domKey) return;
        const targets = m.keys.filter((t) => t && t.length >= MIN_SUFFIX);
        if (targets.length === 0) return;
        let found = null;
        let count = 0;
        domMap.forEach((v, k) => {
            if (k.length < MIN_SUFFIX || claims.has(k)) return; // exact matches own their control
            if (targets.some((t) => t.endsWith(k) || k.endsWith(t))) { found = k; count += 1; }
        });
        if (count !== 1) return;
        if (!wantedBy.has(found)) wantedBy.set(found, []);
        wantedBy.get(found).push(i);
    });
    wantedBy.forEach((idxs, domKey) => {
        if (idxs.length !== 1) return; // several columns share the suffix — never guess
        metas[idxs[0]].domKey = domKey;
        claims.set(domKey, 1);
    });

    return metas.map((m) => {
        const dom = m.domKey ? domMap.get(m.domKey) : undefined;
        const unique = m.domKey ? claims.get(m.domKey) === 1 : false;
        const onScreen = !!(dom && dom.onScreen && unique);
        const curated = typeof m.f.uiLabel === 'string' ? m.f.uiLabel.trim() : '';
        if (curated) return { ...m.f, uiLabel: curated, uiLabelSource: 'curated', onScreen };
        if (dom && dom.label) return { ...m.f, uiLabel: dom.label, uiLabelSource: 'screen', onScreen };
        const i18nLabel = exactLookup(i18nIndex, m.keys);
        if (i18nLabel) return { ...m.f, uiLabel: i18nLabel, uiLabelSource: 'i18n', onScreen };
        return { ...m.f, uiLabel: null, uiLabelSource: null, onScreen };
    });
};
